/**
 * The `?next=` a sign-in may return to, or undefined when it must be ignored.
 *
 * The guide (docs/spec/guide.md §3.3) sends a signed-out reader to
 * `/login?next=/guide/...`, and sign-in goes back there. Anything else that
 * lands in `next` is attacker-controlled, so only a path on THIS origin is
 * honoured: it starts with one `/` and not two, has no backslash (browsers
 * read `/\evil.example` as `//evil.example`), no control character, and
 * still resolves to this origin.
 *
 * And it must be a guide path. The guide is the only caller, and a `next`
 * into the product would let a crafted link land someone on a route that
 * acts the moment it renders (`/q/…`, `/accept-invite`) straight after they
 * typed their password, and would skip `landingRoute()`'s onboarding gate.
 * Widen this only when a second caller needs it.
 */
export function safeNextPath(raw: string | null | undefined, origin: string): string | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;
  if (!raw.startsWith("/") || raw.startsWith("//")) return undefined;
  if (raw.includes("\\")) return undefined;
  // eslint-disable-next-line no-control-regex -- refusing control characters is the point
  if (/[\u0000-\u001f\u007f]/u.test(raw)) return undefined;
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return undefined;
  }
  if (url.origin !== origin) return undefined;
  const path = `${url.pathname}${url.search}${url.hash}`;
  return isServerPage(path) ? path : undefined;
}

/** Whether a path belongs to a surface the SPA router does not own, so the
 * browser must load it rather than the router render it. */
export function isServerPage(path: string): boolean {
  return (
    path === "/guide" ||
    path.startsWith("/guide/") ||
    path.startsWith("/guide?") ||
    path.startsWith("/guide#")
  );
}
